import React, { useEffect, useMemo } from 'react'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { LIST_ITEM_COPY } from 'src/components/Reusable/ListItem/ListItem.constants'
import { PolicyInfo } from 'src/components/Reusable/ListItem/ListItem.components'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { ModalForm, ModalFormId } from '../../..'
import { useFrameworkWizard } from '../../../../../context/FormContext'
import { ReusablePoliciesModal } from '../Policies/ReusablePoliciesModal'
import { NoRecommendedTemplates } from '../NoRecommendedTemplates'
import { MultiSelectForm } from '../../../../Reusable/Forms/MultiSelectForm'
import { Spinner } from '../../../../../Throbber/Spinner'
import { useDepartmentForComplianceFramework } from '../../../../async/complianceframework/useDepartmentForComplianceFramework'
import { usePolicyForComplianceFramework } from '../../../../async/complianceframework/usePolicyForComplianceFramework'
import {
  SelectControlsRowItem,
  SelectControlsRowItemBaseProps,
} from '../../../../Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import {
  DepartmentChipWithDepartment,
  DepartmentSummaryBadge,
} from '../../../../../pages/Controls/DepartmentChip'
import { BasicChip } from '../../../../Reusable/Chips/BasicChip/BasicChip'

interface SelectControlsForComplianceFrameworkProps
  extends SelectControlsRowItemBaseProps {
  complianceFrameworkId: string
}

// Legacy fallback: per-row policy RPC. Engaged only when the control proto
// doesn't carry LinkedPolicies
const LegacyPolicyInfo = ({
  policyModelId,
  complianceFrameworkId,
}: {
  policyModelId: string
  complianceFrameworkId: string
}) => {
  const shouldFetch = policyModelId !== ''
  const { data, isLoading } = usePolicyForComplianceFramework(
    policyModelId,
    complianceFrameworkId,
    shouldFetch,
  )
  if (!shouldFetch) {
    return <PolicyInfo policyName={LIST_ITEM_COPY.NO_POLICY} isWarning={true} />
  }
  if (isLoading) {
    return <Spinner color="primary" size="m" />
  }
  return (
    <PolicyInfo
      policyName={data?.getName() || LIST_ITEM_COPY.NO_POLICY}
      isWarning={!data}
    />
  )
}

// Legacy fallback: per-row dept RPC. Engaged only when the control proto
// doesn't carry an inline department short_name.
const LegacyDepartmentChip = ({
  departmentId,
  complianceFrameworkId,
}: {
  departmentId: string
  complianceFrameworkId: string
}) => {
  const shouldFetch = departmentId !== ''
  const department = useDepartmentForComplianceFramework(
    departmentId,
    complianceFrameworkId,
    shouldFetch,
  )

  if (!shouldFetch) {
    return <BasicChip>-</BasicChip>
  }
  return <DepartmentChipWithDepartment department={department} />
}

const SelectControlsForComplianceFramework = ({
  control,
  setSelectedControlIds,
  selectedControlIds,
  complianceFrameworkId,
}: SelectControlsForComplianceFrameworkProps) => {
  const isMultiPolicyEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.MULTI_POLICY_CONTROL_LINKAGES,
  )
  const linkedPolicies = control.getLinkedPoliciesList()
  const hasInlinePolicies = linkedPolicies.length > 0

  const policyNames = useMemo(() => {
    if (!hasInlinePolicies) return []
    if (isMultiPolicyEnabled) {
      return linkedPolicies.map((p) => p.getPolicyName()).filter(Boolean)
    }
    const first = linkedPolicies[0].getPolicyName()
    return first ? [first] : []
  }, [hasInlinePolicies, isMultiPolicyEnabled, linkedPolicies])

  const departmentNames = useMemo(() => {
    if (!hasInlinePolicies) return []
    if (isMultiPolicyEnabled) {
      return Array.from(
        new Set(linkedPolicies.map((p) => p.getDepartment()).filter(Boolean)),
      )
    }
    const inlineShortName = control.getDepartmentShortName()
    return inlineShortName ? [inlineShortName] : []
  }, [hasInlinePolicies, isMultiPolicyEnabled, linkedPolicies, control])

  return (
    <SelectControlsRowItem
      {...{ control, setSelectedControlIds, selectedControlIds }}
      customPolicyElement={
        policyNames.length > 1 ? (
          <Tooltip
            id={`select-controls-policies-tooltip-${control.getId()}`}
            placement={TooltipPositions.top}
            overlayType={TooltipOverlayType.popover}
            tooltipBody={policyNames.join(', ')}
          >
            <div style={{ width: '100%', minWidth: 0 }}>
              <PolicyInfo
                policyName={policyNames.join(', ')}
                isWarning={false}
              />
            </div>
          </Tooltip>
        ) : policyNames.length === 1 ? (
          <PolicyInfo policyName={policyNames[0]} isWarning={false} />
        ) : (
          <LegacyPolicyInfo
            policyModelId={control.getPolicyId()}
            complianceFrameworkId={complianceFrameworkId}
          />
        )
      }
      customDepartmentElement={
        departmentNames.length > 0 ? (
          isMultiPolicyEnabled ? (
            <DepartmentSummaryBadge
              departmentNames={departmentNames}
              subjectId={control.getId()}
            />
          ) : (
            <BasicChip>{departmentNames[0]}</BasicChip>
          )
        ) : (
          <LegacyDepartmentChip
            departmentId={control.getDepartmentId()}
            complianceFrameworkId={complianceFrameworkId}
          />
        )
      }
      modelIdAsId
    />
  )
}

export const SelectControlsModal = (): JSX.Element => {
  const {
    controlsState: {
      suggestedControls,
      selectedControls,
      setSelectedControls,
      controlsInAccount,
    },
    frameworkState: { framework },
    loadingState: { loading },
  } = useFrameworkWizard()

  const hide = useHideModal({ modalId: ModalFormId.SELECT_CONTROLS })
  const show = useIsShowModal(ModalFormId.SELECT_CONTROLS)

  useEffect(() => {
    setSelectedControls(suggestedControls.map((elem) => elem.getModelId()))
  }, [setSelectedControls, suggestedControls])

  const handleDeselectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedControls([])
  }
  const handleSelectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedControls(suggestedControls.map((elem) => elem.getModelId()))
  }

  const multiSelectFormProps = {
    formId: ModalFormId.SELECT_CONTROLS,
    title:
      'We recommend adding all of these controls. You can always change this later.',
    loading,
    handleSelectAll,
    handleDeselectAll,
    gridTemplateColumnStyling:
      'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)',
    gridItems: suggestedControls.length ? (
      <>
        {suggestedControls.map((control) => (
          <SelectControlsForComplianceFramework
            key={control.getId()}
            control={control}
            setSelectedControlIds={setSelectedControls}
            selectedControlIds={selectedControls}
            complianceFrameworkId={framework?.getModelId() || ''}
          />
        ))}
      </>
    ) : (
      <NoRecommendedTemplates templateType={'controls'} />
    ),
  }

  return (
    <div>
      <ReusablePoliciesModal />
      <ModalForm
        show={show}
        hide={hide}
        formId={ModalFormId.SELECT_CONTROLS}
        size="xl"
        title={`Add Controls for ${framework?.getName() || ''}`}
        onBack={useSetActiveModal(
          controlsInAccount.length
            ? ModalFormId.REUSABLE_CONTROLS
            : ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK,
        )}
        customSubmitButton={
          <StandardOpenModalButton modalId={ModalFormId.REUSABLE_POLICIES} />
        }
      >
        <MultiSelectForm showSelectButtons {...multiSelectFormProps} />
      </ModalForm>
    </div>
  )
}
